import Calculator from './calculator';
import AntAuth from './AntAuth';
import AntTime from './AntTime.js';
import cs from './const.js';
import hash from 'hash-sum';
import stringify from './stableStringify';



// Cache điều kiện trong bộ nhớ (session-local) — thay localStorage đồng bộ (đọc/ghi ổ đĩa mỗi lần).
const _cacheData = {};

// Khoá điều kiện theo setting / phân quyền / phiên (không phụ thuộc dữ liệu) và theo dữ liệu.
const ACCESS_CONDITION_KEYS = [
    'whenSys',
    'whenSysType',
    'whenUser',
    'whenUserType',
    'whenSysAndUser',
    'whenSysAndUserType',
    'whenSysOrUser',
    'whenSysOrUserType',
    'whenSession',
    'whenSessionType',
    'whenUserScreen',
    'whenUserScreens'
];
const DATA_CONDITION_KEYS = ['when', 'whenCustom'];
// Memo băm luật tĩnh theo tham chiếu (luật config load 1 lần → WeakMap hit), tránh stringify luật mỗi lần.
const _ruleHashMap = new WeakMap();
function _ruleHash(rule) {
    if (rule === null || typeof rule !== 'object') return hash(stringify(rule));
    let h = _ruleHashMap.get(rule);
    if (h === undefined) {
        h = hash(stringify(rule));
        _ruleHashMap.set(rule, h);
    }
    return h;
}
// Vân tay ngữ cảnh phiên (session trừ settings) — băm 1 lần/phiên theo tham chiếu sessionInfo.
const _ctxHashMap = new WeakMap();
function _ctxHash(sessionInfo) {
    if (!sessionInfo || typeof sessionInfo !== 'object') return hash(stringify(sessionInfo));
    let h = _ctxHashMap.get(sessionInfo);
    if (h === undefined) {
        const noS = { ...sessionInfo };
        delete noS.user_settings;
        delete noS.bus_settings;
        h = hash(stringify(noS));
        _ctxHashMap.set(sessionInfo, h);
    }
    return h;
}

const AntCondition = {
    getValueFromSession(cond) {
        if (cond === undefined) return cond;

        const userSettings = AntAuth.getSessionInfo('user_settings');
        const busSettings = AntAuth.getSessionInfo('bus_settings');

        if (typeof cond === 'string' || typeof cond === 'number' || typeof cond === 'boolean') return cond;

        if (cond.fromUser) {
            const fieldName = cond.fromUser.field || cond.fromUser.dataField;
            return fieldName ? userSettings?.[fieldName] : undefined;
        }
        if (cond.fromSys) {
            const fieldName = cond.fromSys.field || cond.fromSys.dataField;
            return fieldName ? busSettings?.[fieldName] : undefined;
        }
        if (cond.fromSession) {
            const fieldName = cond.fromSession.field || cond.fromSession.dataField;
            return fieldName ? AntAuth.getSessionInfo(fieldName) : undefined;
        }

        return cond;
    },

    getValue(obj, cond) {
        if (cond === undefined) return undefined;
        if (typeof cond !== 'object' && !Array.isArray(cond)) return cond;

        if (cond && typeof cond === 'object' && !Array.isArray(cond) && cond.value === undefined) return cond;

        const conds = Array.isArray(cond) ? cond.map((v) => ({ ...v })) : [{ ...cond }];
        if (conds.length === 0) return undefined;

        const userSettings = AntAuth.getSessionInfo('user_settings');
        const busSettings = AntAuth.getSessionInfo('bus_settings');

        for (const v of conds) {
            if (typeof v === 'object' && v !== null) {
                const isOK = this.isValid(obj, {
                    when: v.when,
                    whenUser: v.whenUser,
                    whenSys: v.whenSys,
                    whenSysAndUser: v.whenSysAndUser,
                    whenSysOrUser: v.whenSysOrUser,
                    whenSession: v.whenSession,
                    whenUserScreen: v.whenUserScreen,
                    whenUserScreens: v.whenUserScreens,
                    whenCustom: v.whenCustom,
                    whenType: v.whenType,
                    whenUserType: v.whenUserType,
                    whenSysAndUserType: v.whenSysAndUserType,
                    whenSysOrUserType: v.whenSysOrUserType,
                    whenSessionType: v.whenSessionType
                });

                if (isOK && v.value !== undefined) {
                    const val = v.value;
                    if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') return val;
                    if (val && typeof val === 'object') {
                        if (val.fromUser) {
                            const fieldName = val.fromUser.field || val.fromUser.dataField;
                            if (userSettings?.[fieldName] !== undefined) return userSettings[fieldName];
                        }
                        if (val.fromSys) {
                            const fieldName = val.fromSys.field || val.fromSys.dataField;
                            if (busSettings?.[fieldName] !== undefined) return busSettings[fieldName];
                            const sessionVal = AntAuth.getSessionInfo(fieldName);
                            if (sessionVal !== undefined) return sessionVal;
                        }
                        if (val.fromSession) {
                            const fieldName = val.fromSession.field || val.fromSession.dataField;
                            const sessionVal = AntAuth.getSessionInfo(fieldName);
                            if (sessionVal !== undefined) return sessionVal;
                        }
                    }
                    if (val !== undefined) return val;
                }
            }
        }

        return undefined;
    },

    isValid(obj, cond) {
        if (cond === undefined || cond === null || cond === true) return true;
        if (cond === false) return false;

        if (typeof cond !== 'object' && !Array.isArray(cond)) return true;

        const conds = Array.isArray(cond) ? cond.map((v) => ({ ...v })) : [{ ...cond }];
        if (conds.length === 0) return false;

        for (const v of conds) {
            if (this.isValidOne(obj, v)) return true;
        }

        return false;
    },

    isValidOne(obj, cond) {
        const sessionInfo = AntAuth.getSessionInfo() || {};
        const { user_settings: userSettings = {}, bus_settings: busSettings = {} } = sessionInfo;

        // Context-specific info without settings (preserving exact clone behavior)
        const infoNoSettings = { ...sessionInfo };
        delete infoNoSettings.user_settings;
        delete infoNoSettings.bus_settings;

        // Vân tay ngữ cảnh phiên (1 lần/phiên) + biến thể theo checksum settings.
        // Thiếu checksum → fallback băm đầy đủ (giữ đúng hành vi cũ). Khoá mới gom nhóm y hệt khoá cũ.
        const ctx = _ctxHash(sessionInfo);
        const uCk = userSettings.userSettingChecksum;
        const ugCk = userSettings.userGroupSettingChecksum;
        const bCk = busSettings.busSettingChecksum;
        const userVariant =
            uCk != null && ugCk != null ? `u:${uCk}|ug:${ugCk}|c:${ctx}` : `f:${hash(stringify({ ...userSettings, ...infoNoSettings }))}`;
        const sysVariant = bCk != null ? `b:${bCk}|c:${ctx}` : `f:${hash(stringify({ ...busSettings, ...infoNoSettings }))}`;
        const allVariant =
            uCk != null && ugCk != null && bCk != null
                ? `b:${bCk}|u:${uCk}|ug:${ugCk}|c:${ctx}`
                : `f:${hash(stringify({ ...userSettings, ...busSettings, ...infoNoSettings }))}`;
        const sessVariant = `c:${ctx}`;

        let ok1 = true,
            ok2 = true,
            ok3 = true,
            ok4 = true,
            ok5 = true,
            ok6 = true,
            ok7 = true,
            ok8 = true;

        if (cond.when && typeof cond.when === 'object') {
            ok1 = this._isValid(obj, cond.when, cond.whenType);
            if (!ok1) return false;
        }

        const getCachedResult = (prefix, dataFn, when, whenType, variant) => {
            const hashKey = `${cs.System_Code}|${prefix}|${whenType || ''}|${_ruleHash(when)}|${variant}`;
            const cValue = _cacheData[hashKey];
            if (cValue !== undefined) return cValue === true;

            const result = this._isValid(dataFn(), when, whenType, true);
            _cacheData[hashKey] = result;
            return result;
        };

        if (cond.whenUser && typeof cond.whenUser === 'object') {
            ok2 = getCachedResult('whenUser', () => ({ ...userSettings, ...infoNoSettings }), cond.whenUser, cond.whenUserType, userVariant);
            if (!ok2) return false;
        }

        if (cond.whenSys && typeof cond.whenSys === 'object') {
            ok3 = getCachedResult('whenSys', () => ({ ...busSettings, ...infoNoSettings }), cond.whenSys, cond.whenSysType, sysVariant);
            if (!ok3) return false;
        }

        if (cond.whenSysAndUser && typeof cond.whenSysAndUser === 'object') {
            const hashKey = `${cs.System_Code}|whenSysAndUser|${cond.whenSysAndUserType || ''}|${_ruleHash(cond.whenSysAndUser)}|${allVariant}`;
            const cValue = _cacheData[hashKey];
            if (cValue !== undefined) {
                ok4 = cValue === true;
            } else {
                const okS = this._isValid({ ...busSettings, ...infoNoSettings }, cond.whenSysAndUser, cond.whenSysAndUserType, true);
                const okU = this._isValid({ ...userSettings, ...infoNoSettings }, cond.whenSysAndUser, cond.whenSysAndUserType, true);
                ok4 = ok1 && okS && okU;
                _cacheData[hashKey] = ok4;
            }
            if (!ok4) return false;
        }

        if (cond.whenSysOrUser && typeof cond.whenSysOrUser === 'object') {
            const hashKey = `${cs.System_Code}|whenSysOrUser|${cond.whenSysOrUserType || ''}|${_ruleHash(cond.whenSysOrUser)}|${allVariant}`;
            const cValue = _cacheData[hashKey];
            if (cValue !== undefined) {
                ok5 = cValue === true;
            } else {
                const okU = this._isValid({ ...userSettings, ...infoNoSettings }, cond.whenSysOrUser, cond.whenSysOrUserType, true);
                const okS = this._isValid({ ...busSettings, ...infoNoSettings }, cond.whenSysOrUser, cond.whenSysOrUserType, true);
                ok5 = ok1 && (okU || okS);
                _cacheData[hashKey] = ok5;
            }
            if (!ok5) return false;
        }

        if (cond.whenSession && typeof cond.whenSession === 'object') {
            ok6 = getCachedResult('whenSession', () => infoNoSettings, cond.whenSession, cond.whenSessionType, sessVariant);
            if (!ok6) return false;
        }

        if (typeof cond.whenUserScreen === 'string') {
            ok7 = Array.isArray(userSettings.access_screens) && userSettings.access_screens.includes(cond.whenUserScreen);
            if (!ok7) return false;
        }

        if (Array.isArray(cond.whenUserScreens)) {
            ok7 = cond.whenUserScreens.some((v) => Array.isArray(userSettings.access_screens) && userSettings.access_screens.includes(v));
            if (!ok7) return false;
        }

        if (typeof cond.whenCustom === 'function') {
            ok8 = !!cond.whenCustom(obj);
            if (!ok8) return false;
        }

        return ok1 && ok2 && ok3 && ok4 && ok5 && ok6 && ok7 && ok8;
    },

    _isValid(obj, cond, dfCondType, undefinedIsZero) {
        if (!cond || typeof cond !== 'object' || Object.keys(cond).length === 0) return true;

        const EXCLUDED_KEYS = new Set(['whenType', 'whenUserType', 'whenSysType', 'whenSysAndUserType', 'whenSysOrUserType', 'whenSessionType']);
        const ruleKeys = Object.keys(cond).filter((k) => cond[k] !== undefined && !EXCLUDED_KEYS.has(k));

        if (ruleKeys.length === 0) return true;

        const condType = dfCondType ? String(dfCondType) : 'AND';

        for (const itemKey of ruleKeys) {
            const isValid = this._isValidOne(obj, cond, itemKey, undefinedIsZero);

            if (condType === 'OR' && isValid) return true;
            if (condType === 'OR-NOT' && isValid) return false;
            if (condType === 'AND' && !isValid) return false;
            if (condType === 'AND-NOT' && !isValid) return true;
        }

        switch (condType) {
            case 'OR':
                return false;
            case 'OR-NOT':
                return true;
            case 'AND':
                return true;
            case 'AND-NOT':
                return false;
            default:
                return false;
        }
    },

    _isValidOne(obj, cond, itemKey, undefinedIsZero) {
        if (!cond) return true;

        const itemValue = cond[itemKey];
        const sessionInfo = AntAuth.getSessionInfo() || {};
        const { user_settings: userSettings = {}, bus_settings: busSettings = {} } = sessionInfo;

        const infoNoSettings = { ...sessionInfo };
        delete infoNoSettings.user_settings;
        delete infoNoSettings.bus_settings;

        const isObject = typeof itemValue === 'object' && itemValue !== null && !Array.isArray(itemValue);
        const objField = isObject && itemValue.field ? itemValue.field : itemKey;
        let objValue = obj ? obj[objField] : undefined;

        // Recursive / Specialized Logic
        if (isObject) {
            if (itemKey === 'when') return this._isValid(obj, cond.when, cond.whenType);
            if (itemKey === 'whenUser') return this._isValid({ ...userSettings, ...infoNoSettings }, cond.whenUser, cond.whenUserType, true);
            if (itemKey === 'whenSys') return this._isValid({ ...busSettings, ...infoNoSettings }, cond.whenSys, cond.whenSysType, true);

            if (itemKey === 'whenSysAndUser') {
                return (
                    this._isValid({ ...userSettings, ...infoNoSettings }, cond.whenSysAndUser, cond.whenSysAndUserType, true) &&
                    this._isValid({ ...busSettings, ...infoNoSettings }, cond.whenSysAndUser, cond.whenSysAndUserType, true)
                );
            }

            if (itemKey === 'whenSysOrUser') {
                return (
                    this._isValid({ ...userSettings, ...infoNoSettings }, cond.whenSysOrUser, cond.whenSysOrUserType, true) ||
                    this._isValid({ ...busSettings, ...infoNoSettings }, cond.whenSysOrUser, cond.whenSysOrUserType, true)
                );
            }

            if (itemKey === 'whenSession') return this._isValid(sessionInfo, cond.whenSession, cond.whenSessionType);
        }

        if (typeof itemValue === 'string' && itemKey === 'whenUserScreen') {
            return !!(userSettings.access_screens && Array.isArray(userSettings.access_screens) && userSettings.access_screens.includes(itemValue));
        }

        if (Array.isArray(itemValue) && itemKey === 'whenUserScreens') {
            return itemValue.some(
                (v) => userSettings.access_screens && Array.isArray(userSettings.access_screens) && userSettings.access_screens.includes(v)
            );
        }

        if (itemKey === 'whenCustom' && typeof itemValue === 'function') {
            return !!itemValue(obj);
        }

        // Generic Condition Block check
        if (
            isObject &&
            (itemValue.when ||
                itemValue.whenUser ||
                itemValue.whenSys ||
                itemValue.whenSysAndUser ||
                itemValue.whenSysOrUser ||
                itemValue.whenSession)
        ) {
            let res1 = true,
                res2 = true,
                res3 = true,
                res4 = true,
                res5 = true,
                res6 = true;

            if (itemValue.when && typeof itemValue.when === 'object') {
                res1 = this._isValid(obj, itemValue.when, itemValue.whenType);
                if (!res1) return false;
            }
            if (itemValue.whenUser && typeof itemValue.whenUser === 'object') {
                res2 = this._isValid({ ...userSettings, ...infoNoSettings }, itemValue.whenUser, itemValue.whenUserType, true);
                if (!res2) return false;
            }
            if (itemValue.whenSys && typeof itemValue.whenSys === 'object') {
                res3 = this._isValid({ ...busSettings, ...infoNoSettings }, itemValue.whenSys, itemValue.whenSysType, true);
                if (!res3) return false;
            }
            if (itemValue.whenSysAndUser && typeof itemValue.whenSysAndUser === 'object') {
                const okU = this._isValid({ ...userSettings, ...infoNoSettings }, itemValue.whenSysAndUser, itemValue.whenSysAndUserType, true);
                const okS = this._isValid({ ...busSettings, ...infoNoSettings }, itemValue.whenSysAndUser, itemValue.whenSysAndUserType, true);
                res4 = res1 && okU && okS;
                if (!res4) return false;
            }
            if (itemValue.whenSysOrUser && typeof itemValue.whenSysOrUser === 'object') {
                const okU = this._isValid({ ...userSettings, ...infoNoSettings }, itemValue.whenSysOrUser, itemValue.whenSysOrUserType, true);
                const okS = this._isValid({ ...busSettings, ...infoNoSettings }, itemValue.whenSysOrUser, itemValue.whenSysOrUserType, true);
                res5 = res1 && (okU || okS);
                if (!res5) return false;
            }
            if (itemValue.whenSession && typeof itemValue.whenSession === 'object') {
                res6 = this._isValid(sessionInfo, itemValue.whenSession, itemValue.whenSessionType);
                if (!res6) return false;
            }
            return res1 && res2 && res3 && res4 && res5 && res6;
        }

        // Special Strings (Strictly preserved)
        if (itemValue === 'IsNull' || itemValue === null) {
            return objValue === null || (Array.isArray(objValue) && objValue.length === 0);
        }
        if (itemValue === 'IsNullOrEmpty') {
            return !objValue || (Array.isArray(objValue) && objValue.length === 0);
        }
        if (itemValue === 'IsNullOrUnique') {
            return !objValue || (Array.isArray(objValue) && objValue.length === 1);
        }
        if (itemValue === 'IsUnique') {
            return Array.isArray(objValue) && objValue.length === 1;
        }
        if (itemValue === 'IsMultiple') {
            return Array.isArray(objValue) && objValue.length > 1;
        }
        if (itemValue === 'NotNull') {
            return objValue !== null && objValue !== undefined && (!Array.isArray(objValue) || objValue.length > 0);
        }
        if (itemValue === 'NotNullAndEmpty') {
            return (
                objValue !== null && objValue !== undefined && Boolean(String(objValue).trim()) && (!Array.isArray(objValue) || objValue.length > 0)
            );
        }
        if (obj && itemKey === 'isTrue' && Array.isArray(itemValue) && itemValue.length === 2) {
            return !!Calculator.execBool(obj, itemValue[0], itemValue[1]);
        }

        // Comparison Core
        if (obj) {
            const compareType = isObject ? itemValue.type : null;
            let value = !isObject
                ? itemValue
                : itemValue.values !== undefined
                ? itemValue.values
                : itemValue.value !== undefined
                ? itemValue.value
                : itemValue.secondField !== undefined
                ? obj[itemValue.secondField]
                : itemValue.userSettingField !== undefined
                ? userSettings[itemValue.userSettingField]
                : itemValue.busSettingField !== undefined
                ? busSettings[itemValue.busSettingField]
                : itemValue;

            if (value === 'NOW') value = AntTime.nowUnix();


            if (!isObject) {
                if (undefinedIsZero && objValue === undefined) objValue = 0;
                if (typeof value === 'boolean') return Boolean(objValue) === Boolean(value);
                if (typeof value === 'number') {
                    if (typeof objValue === 'boolean') return Boolean(objValue) === Boolean(value);
                    return parseFloat(objValue) === parseFloat(value);
                }
                if (Array.isArray(value)) {
                    return value.some(
                        (v) =>
                            v === objValue ||
                            String(v) === String(objValue) ||
                            ((objValue === null || objValue === undefined) &&
                                (v === null || v === 'IsNull' || v === 'undefined' || v === 'IsNullOrEmpty'))
                    );
                }
                if (objValue !== undefined) return objValue === value;
            } else if (itemValue === 'today') {
                return AntTime.now().startOf('day').unix() === AntTime.fromUnix(objValue).startOf('day').unix();
            } else {
                if (itemValue.orNull && objValue === null) return true;
                if (itemValue.onCustomValue) value = itemValue.onCustomValue(itemValue, obj, userSettings, busSettings);
                if (typeof value === 'number' && objValue === undefined) objValue = 0;

                if (objValue !== undefined) {
                    switch (compareType) {
                        case 'diff':
                        case '!=':
                        case '<>':
                            return Array.isArray(value) ? !value.includes(objValue) : objValue !== value;
                        case '>':
                            return typeof objValue === 'number' && objValue > value;
                        case '>=':
                            return typeof objValue === 'number' && objValue >= value;
                        case '<':
                            return typeof objValue === 'number' && objValue < value;
                        case '<=':
                            return typeof objValue === 'number' && objValue <= value;
                        case 'range': {
                            // Preserving specific execution order (overwriting behavior)
                            let res =
                                objValue &&
                                typeof objValue === 'object' &&
                                (objValue.min === undefined || objValue.min <= value) &&
                                (objValue.max === undefined || value <= objValue.max);
                            if (Array.isArray(objValue) && objValue.length === 2) res = objValue[0] <= value && value <= objValue[1];
                            return res;
                        }
                        case 'includes':
                            return Array.isArray(objValue) && objValue.includes(value);
                        case 'contains':
                            return typeof objValue === 'string' && objValue.includes(String(value));
                        case 'minLength':
                            return typeof objValue === 'string' && objValue.length >= parseInt(value);
                        case 'maxLength':
                            return typeof objValue === 'string' && objValue.length <= parseInt(value);
                        case 'lastSeconds':
                            return AntTime.diffSecondToNow(AntTime.init(objValue).unix()) <= value;
                        case 'afterSeconds':
                            return AntTime.diffSecondToNow(AntTime.init(objValue).unix()) > value;
                        case 'weekday': {
                            const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                            const dayName = days[parseInt(AntTime.fromUnix(value).isoWeekday()) - 1];
                            return (Array.isArray(objValue) && objValue.includes(dayName)) || objValue === dayName;
                        }
                        default:
                            return objValue === value;
                    }
                }
            }
        }

        return false;
    },

    isVisible(obj, comp) {
        if (!comp) return false;
        const condV = comp.visibles !== undefined ? comp.visibles : comp.visible;
        if (condV !== undefined) return this.isValid(obj, condV);

        const condH = comp.hiddens !== undefined ? comp.hiddens : comp.hidden;
        if (condH !== undefined) return !this.isValid(obj, condH);

        if (comp.dataField && obj?.visible && typeof obj.visible === 'object' && Array.isArray(obj.visible.visible_fields)) {
            return obj.visible.visible_fields.includes(comp.dataField);
        }
        return true;
    },

    // Điều kiện được phép vào "Máy bán hàng" (POS): có quyền view_Module_POS VÀ truy cập ≥1 màn POS.
    // Nguồn dùng chung cho nút 'Máy bán hàng' (AntMainHeader) + nút chuyển POS (AntVoucher) → luôn đồng nhất.
    canUsePOS() {
        // Nhóm "Chỉ Kế toán" không vào Máy bán hàng.
        if (AntAuth.isWorkspaceModeLocked()) return false;
        const okPerm = this.isVisible(null, { visible: { whenUser: { view_Module_POS: 1 } } });
        if (!okPerm) return false;
        return this.isVisible(null, {
            visible: {
                whenUserScreens: ['AROrders', 'ARItemStatuses', 'ARServeCookings', 'ARServeServices', 'ARAppointments', 'ARRentalRoomBookings']
            }
        });
    },

    // Được vào không gian Kế toán (màn AccountingWorkspace) — dùng cho nút chuyển chế độ trên header.
    canUseAccounting() {
        return AntAuth.canUseAccountingMode();
    },

    // Bị chặn bởi SETTING / PHÂN QUYỀN / PHIÊN (không phụ thuộc dữ liệu dòng) — dùng để ẩn "bằng mọi giá".
    // Chỉ xét các khoá whenSys*, whenUser*, whenSession*, whenUserScreen(s); bỏ qua điều kiện theo dữ liệu (when,
    // whenCustom) vì không kết luận được ở mức cột.
    //   visible/visibles: mọi nhánh (OR) đều trượt phần quyền/setting → chặn.
    //   hidden/hiddens: có nhánh CHỈ gồm điều kiện quyền/setting và đúng → chặn.
    isAccessDenied(comp) {
        if (!comp || typeof comp !== 'object') return false;
        const pickAccess = (branch) => {
            const picked = {};
            ACCESS_CONDITION_KEYS.forEach((key) => {
                if (branch && branch[key] !== undefined) picked[key] = branch[key];
            });
            return picked;
        };
        const hasAccess = (branch) => Boolean(branch) && typeof branch === 'object' && ACCESS_CONDITION_KEYS.some((key) => branch[key] !== undefined);
        const hasData = (branch) => Boolean(branch) && typeof branch === 'object' && DATA_CONDITION_KEYS.some((key) => branch[key] !== undefined);

        const condV = comp.visibles !== undefined ? comp.visibles : comp.visible;
        if (condV && typeof condV === 'object') {
            const branches = Array.isArray(condV) ? condV : [condV];
            if (branches.length && branches.some(hasAccess) && !branches.some((b) => !hasAccess(b) || this.isValidOne(null, pickAccess(b))))
                return true;
        }

        const condH = comp.hiddens !== undefined ? comp.hiddens : comp.hidden;
        if (condH === true) return true;
        if (condH && typeof condH === 'object') {
            const branches = Array.isArray(condH) ? condH : [condH];
            if (branches.some((b) => hasAccess(b) && !hasData(b) && this.isValidOne(null, pickAccess(b)))) return true;
        }
        return false;
    },

    isHidden(obj, comp) {
        if (!comp) return true;
        const condH = comp.hiddens !== undefined ? comp.hiddens : comp.hidden;
        if (condH !== undefined) return this.isValid(obj, condH);

        const condV = comp.visibles !== undefined ? comp.visibles : comp.visible;
        if (condV !== undefined) return !this.isValid(obj, condV);

        if (comp.dataField && obj?.visible && typeof obj.visible === 'object' && Array.isArray(obj.visible.visible_fields)) {
            return !obj.visible.visible_fields.includes(comp.dataField);
        }
        return false;
    },

    isEditable(obj, comp) {
        if (!comp) return true;
        const condE = comp.editables !== undefined ? comp.editables : comp.editable;
        if (condE !== undefined) return this.isValid(obj, condE);

        const condD = comp.disables !== undefined ? comp.disables : comp.disable;
        if (condD !== undefined) return !this.isValid(obj, condD);

        return false;
    },

    isRender(obj, comp) {
        if (!comp) return false;
        const cond = comp.renders !== undefined ? comp.renders : comp.render;
        return cond === undefined ? true : this.isValid(obj, cond);
    },

    isDisable(obj, comp) {
        if (!comp) return true;
        const condD = comp.disables !== undefined ? comp.disables : comp.disable;
        if (condD !== undefined) return this.isValid(obj, condD);

        const condE = comp.editables !== undefined ? comp.editables : comp.editable;
        if (condE !== undefined) return !this.isValid(obj, condE);

        return false;
    },

    isHighlight(obj, comp) {
        if (!comp) return false;
        const cond = comp.highlights !== undefined ? comp.highlights : comp.highlight;
        return cond === undefined ? false : this.isValid(obj, cond);
    },

    setDefault(obj, comp) {
        if (!obj) return;
        const value = this.getDefault(obj, comp);
        if (value) {
            obj[comp.dataField] = value;
            if (
                comp.editorType === 'antDropDownGrid' &&
                String(comp.dataField).endsWith('_code') &&
                typeof comp.default === 'object' &&
                comp.default !== null &&
                typeof comp.default.value === 'object' &&
                comp.default.value !== null
            ) {
                const objD = JSON.parse(stringify(comp.default.value).replace('_code', '_name'));
                const dispValue = this.getDefault(obj, { ...comp, default: objD });
                if (dispValue) {
                    obj[String(comp.dataField).replace('_code', '_name')] = dispValue;
                }
            }
        }
    },

    getDefault(obj, comp) {
        if (comp.default === undefined) return undefined;
        if (typeof comp.default !== 'object' && !Array.isArray(comp.default)) return comp.default;

        const defaults = Array.isArray(comp.default) ? comp.default.map((v) => ({ ...v })) : [{ ...comp.default }];
        const sessionInfo = AntAuth.getSessionInfo() || {};
        const { user_settings: userSettings = {}, bus_settings: busSettings = {} } = sessionInfo;

        const infoNoSettings = { ...sessionInfo };
        delete infoNoSettings.user_settings;
        delete infoNoSettings.bus_settings;

        if (defaults.length === 0) return undefined;

        for (const v of defaults) {
            if (typeof v === 'object' && v !== null) {
                let ok1 = true,
                    ok2 = true,
                    ok3 = true,
                    ok4 = true,
                    ok5 = true;
                if (obj && v.when && typeof v.when === 'object') ok1 = this._isValid(obj, v.when, v.whenType);
                if (v.whenUser && typeof v.whenUser === 'object')
                    ok2 = this._isValid({ ...userSettings, ...infoNoSettings }, v.whenUser, v.whenUserType, true);
                if (v.whenSys && typeof v.whenSys === 'object')
                    ok3 = this._isValid({ ...busSettings, ...infoNoSettings }, v.whenSys, v.whenSysType, true);

                if (v.whenSysAndUser && typeof v.whenSysAndUser === 'object') {
                    ok2 = this._isValid({ ...userSettings, ...infoNoSettings }, v.whenSysAndUser, v.whenSysAndUserType, true);
                    ok3 = this._isValid({ ...busSettings, ...infoNoSettings }, v.whenSysAndUser, v.whenSysAndUserType, true);
                    ok4 = ok1 && ok2 && ok3;
                }
                if (v.whenSysOrUser && typeof v.whenSysOrUser === 'object') {
                    ok2 = this._isValid({ ...userSettings, ...infoNoSettings }, v.whenSysOrUser, v.whenSysOrUserType, true);
                    ok3 = this._isValid({ ...busSettings, ...infoNoSettings }, v.whenSysOrUser, v.whenSysOrUserType, true);
                    ok5 = ok1 && (ok2 || ok3);
                    ok2 = ok3 = true;
                }

                if (ok1 && ok2 && ok3 && ok4 && ok5 && v.value !== undefined) {
                    const val = v.value;
                    if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') return val;

                    const getDisplayValue = (settings, field) => {
                        if (String(field).endsWith('_code')) {
                            const dispField = String(field).replace('_code', '_name');
                            if (settings[dispField] !== undefined) return { value: settings[field], display: settings[dispField] };
                        }
                        return settings[field];
                    };

                    if (val.fromUser && val.fromUser.field && userSettings[val.fromUser.field] !== undefined)
                        return getDisplayValue(userSettings, val.fromUser.field);
                    if (val.fromUser && val.fromUser.dataField && userSettings[val.fromUser.dataField] !== undefined)
                        return getDisplayValue(userSettings, val.fromUser.dataField);
                    if (val.fromSys) {
                        if (busSettings[val.fromSys.field] !== undefined) return getDisplayValue(busSettings, val.fromSys.field);
                        const sessionVal = AntAuth.getSessionInfo(val.fromSys.field);
                        if (sessionVal !== undefined) return getDisplayValue(sessionInfo, val.fromSys.field);
                    }
                    if (val.fromSession) {
                        const sessionVal = AntAuth.getSessionInfo(val.fromSession.field);
                        if (sessionVal !== undefined) return getDisplayValue(sessionInfo, val.fromSession.field);
                    }
                }
            }
        }
        return undefined;
    },

    toJSON(stExp) {
        return this._toJSON(JSON.parse(this._toJSONString(stExp)));
    },

    _toJSON(ar) {
        if (!Array.isArray(ar) || ar.length === 0) return {};

        if (ar.length === 3 && typeof ar[0] === 'string') {
            const [fieldName, operator, fieldValue] = ar;
            const cleanVal = fieldValue === null || String(fieldValue) === 'null' ? null : fieldValue;

            if (operator === '=') return { [fieldName]: cleanVal };
            if (operator === '<>') return { [fieldName]: cleanVal === null ? 'IsNull' : { type: '<>', value: cleanVal } };
            if (['<', '<=', '>', '>='].includes(operator)) return { [fieldName]: { type: operator, value: cleanVal } };
            if (operator === 'contains') return { [fieldName]: { type: 'contains', value: String(cleanVal) } };
            return {};
        }

        let whenType = 'OR';
        const when = {};
        let groupNum = 0;

        for (const item of ar) {
            if (['and', 'and-not', 'or', 'or-not'].includes(item)) {
                const map = { and: 'AND', 'and-not': 'AND-NOT', or: 'OR', 'or-not': 'OR-NOT' };
                whenType = map[item] || 'OR';
            } else if (Array.isArray(item)) {
                const result = this._toJSON(item);
                if (result && typeof result === 'object') {
                    if (result.when) when[`group_${++groupNum}`] = { ...result };
                    else Object.assign(when, result);
                }
            }
        }
        return { when, whenType };
    },

    _toJSONString(exp, spaces) {
        if (exp && Array.isArray(exp[0])) {
            const TAB_SIZE = 4;
            const currentSpaces = spaces || TAB_SIZE;
            const lineBreak = this._getLineBreak(currentSpaces);
            const content = exp
                .map((item) => (Array.isArray(item?.[0]) ? this._toJSONString(item, currentSpaces + TAB_SIZE) : stringify(item)))
                .join(`,${lineBreak}`);
            return `[${lineBreak}${content}${this._getLineBreak(currentSpaces - TAB_SIZE)}]`;
        }
        return stringify(exp);
    },

    _getLineBreak(spaces) {
        return `\r\n${' '.repeat(Math.max(0, spaces))}`;
    }
};

export default AntCondition;
